import React, { useState } from "react";
import { toast } from "react-toastify";
import { youtubeThumbnail } from "../../utils/emailBrandTemplate";
import { fetchBombBombVideos } from "../../utils/bombbomb";

// video = null | { url, title, thumbnailUrl? }
// BombBomb is the team's provider — its videos carry their own (animated)
// thumbnail via thumbnailUrl; pasted YouTube links derive one from the URL.
export default function VideoPicker({ video, onChange }) {
  const [draftUrl, setDraftUrl] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [showBrowser, setShowBrowser] = useState(false);
  const [bbVideos, setBbVideos] = useState(null); // null = not loaded
  const [bbError, setBbError] = useState("");
  const [loadingBb, setLoadingBb] = useState(false);
  const [search, setSearch] = useState("");

  const openBombBomb = async () => {
    setShowBrowser(true);
    if (bbVideos !== null) return;
    setLoadingBb(true);
    const res = await fetchBombBombVideos();
    setLoadingBb(false);
    if (res.ok) {
      setBbVideos(res.videos);
      setBbError("");
    } else {
      setBbVideos([]);
      setBbError(res.error || "Couldn't load BombBomb videos.");
      if (/not connected/i.test(res.error || "")) {
        toast.info("Connect BombBomb in Settings → BombBomb (Team Lead).");
      }
    }
  };

  const pickBbVideo = (v) => {
    onChange({
      url: v.pageUrl,
      title: v.name,
      thumbnailUrl: v.animatedThumbnail || v.thumbnail || "",
    });
    setShowBrowser(false);
  };

  if (video) {
    const thumb = video.thumbnailUrl || youtubeThumbnail(video.url);
    return (
      <div className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
        {thumb ? (
          <img src={thumb} alt="" className="w-20 h-12 object-cover rounded" />
        ) : (
          <div className="w-20 h-12 rounded bg-slate-800 text-white flex items-center justify-center text-lg">▶</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">🎬 {video.title || "Video"}</p>
          <p className="text-[11px] text-slate-400 truncate">{video.url}</p>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-red-500 font-semibold px-2"
        >
          Remove
        </button>
      </div>
    );
  }

  const filtered = (bbVideos || []).filter((v) =>
    v.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={openBombBomb}
          className="text-xs font-semibold text-white bg-themeColor rounded px-3 py-1.5"
        >
          🎥 From BombBomb
        </button>
        <span className="text-[11px] text-slate-400">or paste a link:</span>
        <input
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder="YouTube/Vimeo link…"
          className="flex-1 min-w-[160px] text-xs rounded border border-slate-300 px-2 py-1.5"
        />
        <input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          placeholder="Title (optional)"
          className="w-36 text-xs rounded border border-slate-300 px-2 py-1.5"
        />
        <button
          type="button"
          disabled={!/^https?:\/\//.test(draftUrl)}
          onClick={() => {
            onChange({ url: draftUrl.trim(), title: draftTitle.trim() });
            setDraftUrl("");
            setDraftTitle("");
          }}
          className="text-xs font-semibold text-slate-600 border border-slate-300 rounded px-3 py-1.5 disabled:opacity-40"
        >
          Add
        </button>
      </div>

      {showBrowser && (
        <div className="border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 p-2 border-b border-slate-100">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your BombBomb videos…"
              className="flex-1 text-xs rounded border border-slate-300 px-2 py-1.5"
            />
            <button
              type="button"
              onClick={() => setShowBrowser(false)}
              className="text-slate-400 hover:text-slate-700 px-1"
            >
              ✕
            </button>
          </div>
          <div className="max-h-56 overflow-auto p-2">
            {loadingBb ? (
              <p className="text-xs text-slate-400 p-2">Loading your videos…</p>
            ) : bbError ? (
              <p className="text-xs text-red-500 p-2">{bbError}</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-400 p-2">
                {search ? "No videos match that search." : "No videos in this BombBomb account yet."}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {filtered.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => pickBbVideo(v)}
                    className="text-left border border-slate-200 rounded-lg overflow-hidden hover:border-sky-400"
                  >
                    {v.thumbnail || v.animatedThumbnail ? (
                      <img
                        src={v.thumbnail || v.animatedThumbnail}
                        alt=""
                        className="w-full h-16 object-cover"
                      />
                    ) : (
                      <div className="w-full h-16 bg-slate-800 text-white flex items-center justify-center">▶</div>
                    )}
                    <p className="text-[11px] font-semibold truncate px-1.5 py-1">{v.name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
